import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import {
  createChunkedBackupFunc,
  downloadBackupFunc,
  clearBackupResult,
} from "../../../store/custom-actions/database-backup-actions/databaseBackupSlicer";
import { Card, Button, Alert, ProgressBar } from "react-bootstrap";

export default function DatabaseBackupPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    create: {
      loading: createLoading,
      data: backupData,
      error: createError,
      chunkedProgress,
    },
    download: { loading: downloadLoading, error: downloadError },
  } = useSelector((state) => state.databaseBackupSlicer);

  const { total, currentIndex, done } = chunkedProgress || {};
  const showProgress = createLoading && total > 0;
  const progressPercent = total > 0 ? Math.round((currentIndex / total) * 100) : 0;

  useEffect(() => {
    return () => dispatch(clearBackupResult());
  }, [dispatch]);

  const handleCreateBackup = (e) => {
    e.preventDefault();
    dispatch(createChunkedBackupFunc());
  };

  const handleDownload = (e) => {
    e.preventDefault();
    dispatch(downloadBackupFunc());
  };

  return (
    <>
      <PageTitle motherMenu={t("Dashboard")} activeMenu={t("Database Backup")} />

      <div className="row">
        <div className="col-12">
          <Card className="card-full">
            <Card.Header>
              <Card.Title className="card-title">{t("Database Backup")}</Card.Title>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                {t("Create a full backup of the application database (chunked, table by table). Only Super Admins can access this feature. After completion, you can download the backup file.")}
              </p>

              {showProgress && (
                <div className="mb-3">
                  <ProgressBar
                    now={progressPercent}
                    label={`${currentIndex} / ${total}`}
                    animated
                    striped
                  />
                  <p className="small text-muted mt-1 mb-0">
                    {t("Exporting tables...")} {currentIndex} / {total}
                  </p>
                </div>
              )}

              {createError && (
                <Alert variant="danger" className="small mb-3">
                  {typeof createError === "string" ? createError : JSON.stringify(createError)}
                </Alert>
              )}
              {downloadError && (
                <Alert variant="danger" className="small mb-3">
                  {typeof downloadError === "string" ? downloadError : JSON.stringify(downloadError)}
                </Alert>
              )}

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button
                  variant="primary"
                  onClick={handleCreateBackup}
                  disabled={createLoading}
                >
                  {createLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {t("Creating...")}
                    </>
                  ) : (
                    t("Create backup")
                  )}
                </Button>

                <Button
                  variant="success"
                  onClick={handleDownload}
                  disabled={downloadLoading}
                >
                  {downloadLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {t("Downloading...")}
                    </>
                  ) : (
                    t("Download latest backup")
                  )}
                </Button>
                {backupData?.filename && (
                  <span className="text-muted small">
                    {t("Latest created")}: {backupData.filename}
                  </span>
                )}
              </div>

              {backupData?.download_url && (
                <p className="mt-2 mb-0 small text-muted">
                  {t("Or open download link in new tab")}:{" "}
                  <a
                    href={backupData.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Download latest backup")}
                  </a>
                </p>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
}
